import { useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Calendar } from '@/components/ui/calendar';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Popover } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { ActivityDetail } from './activity-timeline-detail';
import { ActivityColumnHeaders, ActivityRail } from './activity-timeline-rail';
import { formatDuration } from './activity-timeline.format';
import {
  useActivityDay,
  useActivityRailZoom,
  useActivitySelection,
  useScrollToNowOrStart,
} from './activity-timeline.hooks';
import { type ActivityDayShape } from './activity-timeline.model';
import { RAIL_SCROLL_INSET_PX } from './activity-timeline.zoom';

/**
 * Inside a dialog, such as the day picker's popover, the dialog handles the arrows; everywhere else
 * they select the next or previous capture.
 */
const ARROW_HOTKEY_OPTIONS = {
  preventDefault: true,
  ignoreEventWhen: (event: KeyboardEvent) =>
    event.target instanceof Element &&
    event.target.closest('[role="dialog"]') !== null,
};

const RAIL_WIDTH_CLASS = 'w-[560px]';

const Stat = ({
  label,
  value,
  share,
  hint,
}: {
  label: string;
  value: string;
  share?: string;
  hint: string;
}) => (
  <YStack className="shrink-0 gap-0" title={hint}>
    <span className="text-[11px] leading-4 text-foreground-muted">{label}</span>
    <span className="whitespace-nowrap text-sm leading-5 tabular-nums">
      {value}
      {share === undefined ? null : (
        <span className="text-foreground-muted"> {share}</span>
      )}
    </span>
  </YStack>
);

const DayStats = ({
  shape,
  truncated,
  onShowViolations,
}: {
  shape: ActivityDayShape;
  truncated: boolean;
  onShowViolations: () => void;
}) => {
  const share = (ms: number) =>
    shape.elapsedMs > 0 ? `${Math.round((ms / shape.elapsedMs) * 100)}%` : '0%';

  return (
    <XStack className="items-end justify-between gap-6">
      <XStack className="gap-6 overflow-x-auto">
        <Stat
          label="Observed"
          value={formatDuration(shape.observedMs)}
          share={share(shape.observedMs)}
          hint="Time inside an observation interval, and its share of the day so far"
        />
        <Stat
          label="Present"
          value={formatDuration(shape.presentMs)}
          share={share(shape.presentMs)}
          hint="Time inside a presence interval: the user was at the keyboard"
        />
        <Stat
          label="Attended"
          value={formatDuration(shape.attendedMs)}
          share={share(shape.attendedMs)}
          hint="Focus intersected with presence: on a window, with the user at the keyboard"
        />
        <Stat
          label="Unobserved"
          value={
            shape.unobservedMs === 0
              ? 'none'
              : formatDuration(shape.unobservedMs)
          }
          share={
            shape.unobservedMs === 0 ? undefined : share(shape.unobservedMs)
          }
          hint="Time with no observation interval on any device"
        />
      </XStack>
      <XStack className="shrink-0 items-center gap-3 text-xs text-foreground-muted">
        {truncated ? (
          <span className="text-warning-11">
            the day holds more than this screen shows
          </span>
        ) : null}
        <span className="tabular-nums">
          {shape.snapshotCount} captures · {shape.cardCount} focus intervals
        </span>
        {shape.violations.length === 0 ? null : (
          <Button
            variant="ghost"
            size="xs"
            className="text-warning-11 hover:text-warning-11"
            onClick={onShowViolations}
          >
            {shape.violations.length} invariants broken
          </Button>
        )}
      </XStack>
    </XStack>
  );
};

const ZoomControl = ({
  zoom,
}: {
  zoom: ReturnType<typeof useActivityRailZoom>;
}) => (
  <XStack className="items-center gap-0.5">
    <Button
      variant="ghost"
      size="xs"
      className={cn(zoom.isDefault && 'invisible')}
      onClick={zoom.reset}
    >
      Reset zoom
    </Button>
    <Button
      variant="ghost"
      size="icon-sm"
      title="Zoom out"
      disabled={!zoom.canZoomOut}
      onClick={zoom.zoomOut}
    >
      <Icons.zoomOut />
    </Button>
    <Button
      variant="ghost"
      size="icon-sm"
      title="Zoom in"
      disabled={!zoom.canZoomIn}
      onClick={zoom.zoomIn}
    >
      <Icons.zoomIn />
    </Button>
  </XStack>
);

const DayPicker = ({
  day,
  label,
  onPick,
}: {
  day: Date;
  label: string;
  onPick: (next: Date) => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="ghost" className="gap-1 px-2 text-base font-medium">
          {label}
          <Icons.chevronDown className="size-4 text-foreground-muted" />
        </Button>
      </Popover.Trigger>
      <Popover.Content className="mt-1 w-auto p-0" align="start">
        <div className="p-3">
          <Calendar
            mode="single"
            selected={day}
            defaultMonth={day}
            disabled={{ after: new Date() }}
            onSelect={(next?: Date) => {
              if (!next) return;
              onPick(next);
              setOpen(false);
            }}
            autoFocus
          />
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

const DayNotice = ({ day }: { day: ReturnType<typeof useActivityDay> }) => {
  if (day.isLoading) {
    return <span className="text-sm text-foreground-muted">Loading...</span>;
  }
  if (day.error) {
    return (
      <span className="text-sm text-danger-11">Error: {day.error.message}</span>
    );
  }
  return (
    <span className="text-sm text-foreground-muted">
      Nothing was recorded on {day.label.toLowerCase()}.
    </span>
  );
};

export const ActivityTimeline = () => {
  const day = useActivityDay();
  const zoom = useActivityRailZoom();
  const selection = useActivitySelection(day.shape);
  const [appFilter, setAppFilter] = useState<string | null>(null);
  const activeFilter = day.shape.apps.some((app) => app.appKey === appFilter)
    ? appFilter
    : null;
  const hasTimeline = day.shape.columns.length > 0;

  useScrollToNowOrStart(zoom.scrollRef, day.day, hasTimeline);

  useHotkeys(
    'down,right',
    () => selection.selectAdjacentSnapshot(1),
    ARROW_HOTKEY_OPTIONS,
    [selection.selectAdjacentSnapshot]
  );
  useHotkeys(
    'up,left',
    () => selection.selectAdjacentSnapshot(-1),
    ARROW_HOTKEY_OPTIONS,
    [selection.selectAdjacentSnapshot]
  );

  return (
    <YStack className="h-full min-h-0">
      <YStack className="shrink-0 gap-3 border-b border-border px-5 py-3">
        <XStack className="items-center justify-between">
          <XStack className="items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              title="Previous day"
              onClick={day.goPrev}
            >
              <Icons.chevronLeft />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              title="Next day"
              disabled={day.isToday}
              onClick={day.goNext}
            >
              <Icons.chevronRight />
            </Button>
            <DayPicker day={day.day} label={day.label} onPick={day.pick} />
            <Button
              variant="ghost"
              size="xs"
              className={cn(day.isToday && 'invisible')}
              onClick={day.goToday}
            >
              Today
            </Button>
          </XStack>
          {hasTimeline ? <ZoomControl zoom={zoom} /> : null}
        </XStack>
        {hasTimeline ? (
          <DayStats
            shape={day.shape}
            truncated={day.truncated}
            onShowViolations={selection.clear}
          />
        ) : null}
      </YStack>

      {hasTimeline ? (
        <XStack className="min-h-0 flex-1">
          <YStack className={cn(RAIL_WIDTH_CLASS, 'min-h-0 shrink-0')}>
            <div className="px-5">
              <ActivityColumnHeaders shape={day.shape} />
            </div>
            <div
              ref={zoom.scrollRef}
              className="min-h-0 flex-1 overflow-y-auto px-5 pb-16"
              // The zoom anchor subtracts this padding before scaling, so the two stay one value
              style={{ paddingTop: RAIL_SCROLL_INSET_PX }}
            >
              <ActivityRail
                shape={day.shape}
                dayEnd={day.dayEnd}
                pxPerMinute={zoom.pxPerMinute}
                appFilter={activeFilter}
                selection={{
                  intervalIds: selection.intervalIds,
                  snapshotId:
                    selection.selected?.kind === 'snapshot'
                      ? selection.selected.snapshot.id
                      : null,
                }}
                onSelectInterval={selection.selectInterval}
                onSelectGroup={selection.selectGroup}
                onSelectSnapshot={(snapshot) =>
                  selection.selectSnapshot(snapshot.id)
                }
              />
            </div>
          </YStack>
          <div className="h-full min-w-0 flex-1 border-l border-border">
            <ActivityDetail
              shape={day.shape}
              selection={selection.selected}
              appFilter={activeFilter}
              onFilterApp={setAppFilter}
              onClear={selection.clear}
              onSelectSnapshot={selection.selectSnapshot}
              onSelectInterval={selection.selectInterval}
            />
          </div>
        </XStack>
      ) : (
        <YStack center className="min-h-0 flex-1">
          <DayNotice day={day} />
        </YStack>
      )}
    </YStack>
  );
};
